<template>
  <div class="shortcuts-container">
    <h3 class="section-title">
      🚀 ทางลัด
    </h3>
    <div class="shortcuts-grid">
      <div
        v-for="(item, index) in items"
        :key="index"
        class="shortcut-card"
        @click="handleClick(item)"
      >
        <!-- Badge (top-right corner) -->
        <span
          v-if="getBadgeText(item)"
          class="shortcut-badge"
          :class="getBadgeClass(item)"
        >
          {{ getBadgeText(item) }}
        </span>

        <span class="shortcut-icon">{{ item.icon }}</span>
        <span class="shortcut-label">{{ item.label }}</span>
      </div>
    </div>
  </div>
</template>

<script>
export default {
  name: 'ShortcutGrid',
  props: {
    items: {
      type: Array,
      default: () => [
        { icon: '🎡', label: 'กงล้อ', action: 'wheel' },
        { icon: '👥', label: 'ชวนเพื่อน', action: 'referral' },
        { icon: '✅', label: 'เช็คอิน', action: 'checkin' },
        { icon: '🎫', label: 'คูปองหวย', action: 'lottery-coupon' },
        { icon: '💎', label: 'รับเพชรฟรี', action: 'free-diamond' },
        { icon: '🔑', label: 'กรอก code', action: 'redeem' },
        { icon: '🎁', label: 'โปรโมชั่น', action: 'promotions' }
      ]
    }
  },
  methods: {
    handleClick(item) {
      this.$emit('shortcut-click', item)
    },
    getBadgeText(item) {
      if (!item.badge) return null
      if (typeof item.badge === 'object') return item.badge.text
      if (typeof item.badge === 'number') return item.badge
      return ''
    },
    getBadgeClass(item) {
      const classes = []
      if (typeof item.badge === 'number') {
        classes.push('has-count')
      } else if (typeof item.badge === 'object') {
        classes.push(`badge-${item.badge.variant}`)
        if (item.badge.variant === 'success') {
          classes.push('pulse')
        }
      }
      return classes
    }
  }
}
</script>

<style lang="scss" scoped>
// Mobile-First styles (375px base)
.shortcuts-container {
  padding: 0 12px;

  @media (min-width: 768px) {
    padding: 0 20px;
  }
}

.section-title {
  color: #fff;
  font-size: 1rem;
  font-weight: 600;
  margin-bottom: 12px;

  @media (min-width: 768px) {
    font-size: 1.2rem;
    margin-bottom: 16px;
  }
}

// Mobile-First: 3 columns on mobile
.shortcuts-grid {
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 10px;
  justify-content: start;

  // Tablet (768px+) - 4 columns
  @media (min-width: 768px) {
    grid-template-columns: repeat(4, 1fr);
    gap: 12px;
    max-width: 600px;
  }

  // Desktop (1024px+) - 8 columns (single row)
  @media (min-width: 1024px) {
    grid-template-columns: repeat(8, 1fr);
    gap: 15px;
    max-width: 900px;
  }
}

// Mobile-First: Base styles for mobile (375px) - 3 columns = larger cards
.shortcut-card {
  position: relative;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 15px 10px;
  min-height: 80px;
  border-radius: 12px;
  background: rgba(255, 255, 255, 0.08);
  backdrop-filter: blur(10px);
  -webkit-backdrop-filter: blur(10px);
  border: 1px solid rgba(255, 255, 255, 0.15);
  cursor: pointer;
  transition: all 0.2s ease;
  -webkit-tap-highlight-color: transparent;

  // Touch feedback (mobile)
  &:active {
    transform: scale(0.95);
    background: rgba(var(--theme-primary-rgb), 0.3);
  }

  // Tablet (768px+)
  @media (min-width: 768px) {
    padding: 16px 12px;
    min-height: 90px;
    border-radius: 15px;
  }

  // Desktop hover (1024px+)
  @media (min-width: 1024px) {
    &:hover {
      transform: translateY(-4px);
      background: rgba(115, 103, 240, 0.2);
      border-color: rgba(115, 103, 240, 0.5);
      box-shadow: 0 6px 20px rgba(0, 0, 0, 0.3);
    }

    &:active {
      transform: translateY(-2px);
    }
  }
}

.shortcut-icon {
  font-size: 1.8rem;
  margin-bottom: 6px;
  line-height: 1;

  @media (min-width: 768px) {
    font-size: 2rem;
    margin-bottom: 8px;
  }
}

.shortcut-label {
  color: #fff;
  font-size: 0.8rem;
  font-weight: 500;
  text-align: center;
  line-height: 1.2;

  @media (min-width: 768px) {
    font-size: 0.85rem;
  }
}

// Badge styles
.shortcut-badge {
  position: absolute;
  top: 6px;
  right: 6px;
  min-width: 18px;
  min-height: 18px;
  background: #ef4444;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 0.65rem;
  font-weight: 600;
  color: #fff;

  // Tablet
  @media (min-width: 768px) {
    top: 8px;
    right: 8px;
    min-width: 20px;
    min-height: 20px;
    font-size: 0.7rem;
  }

  // Text badge variants (wider, pill-shaped)
  &.badge-muted {
    background: rgba(255, 255, 255, 0.2);
    color: rgba(255, 255, 255, 0.7);
    min-width: 28px;
    padding: 2px 6px;
    border-radius: 10px;
    font-size: 0.6rem;
  }

  &.badge-warning {
    background: var(--theme-gradient-primary);
    color: #1a1a2e;
    min-width: 28px;
    padding: 2px 6px;
    border-radius: 10px;
    font-size: 0.6rem;
  }

  &.badge-success {
    background: linear-gradient(135deg, #22c55e, #4ade80);
    color: #fff;
    min-width: 28px;
    padding: 2px 6px;
    border-radius: 10px;
    font-size: 0.6rem;
  }

  &.pulse {
    animation: badge-pulse 1.5s ease-in-out infinite;
  }
}

@keyframes badge-pulse {
  0%,
  100% {
    transform: scale(1);
    box-shadow: 0 0 0 0 rgba(34, 197, 94, 0.4);
  }
  50% {
    transform: scale(1.05);
    box-shadow: 0 0 0 6px rgba(34, 197, 94, 0);
  }
}
</style>
